<template>
    <div class="border-b border-border">
        <div class="border-b border-border py-4 flex justify-between">
            <component :is="resources.length == 1 ? 'Link' : 'p'" v-if="!this.showMenu"
                :href="resources.length ? resources[0].url : null" :class="[
                    { 'underline-hover': resources.length == 1 },
                    'text-2xl font-semibold',
                ]" v-text="panelTitle" />
            <ButtonPopoverMenu :items="menuItems">
                <template #button="{ open }">
                    <div :class="[
                        open
                            ? `border-${resourceableModelClass}-accent bg-${resourceableModelClass}-accent text-${resourceableModelClass}`
                            : 'border-transparent',
                        `border hover:border-${resourceableModelClass}-accent
                        h-10 w-10 p-2 rounded-full transition duration-button-hover focus-style`,
                    ]">
                        <PhDotsThreeOutline :class="['flex-grow size-6']" weight="fill" />
                    </div>
                </template>
            </ButtonPopoverMenu>
        </div>
        <div class="divide-y divide-border">
            <ResourcePanelItem v-for="resource in resources.slice(0, maxNbrItems)" :key="resource.id"
                :resource="resource" :resourceableModelClass="resourceableModelClass"
                :hideListTitle="resources.length == 1" :isToTheRight="isToTheRight" />

            <p v-if="resources.length == 0" class="text-center py-8 italic text-text-muted" v-text="$lang.get(
                'No related objects added to this display section, add by pressing to button to the top right'
            )
                " />
        </div>

        <!-- Items that can be shown with toggle -->
        <VerticalSlideTransition :show="showMore">
            <div class="divide-y divide-border border-t border-border">
                <ResourcePanelItem v-for="resource in resourceList.slice(maxNbrItems)" :key="resource.id"
                    :resource="resource" :resourceableModelClass="resourceableModelClass"
                    :isToTheRight="isToTheRight" />
            </div>
        </VerticalSlideTransition>

        <!-- Show more items button -->
        <div v-if="resources.length > maxNbrItems && !this.isEditing"
            class="pt-4 flex justify-end border-t border-border pb-2">
            <button
                class="flex items-center gap-x-2 text-sm group hover:scale-105 transition-transform duration-button-hover ease-in-out"
                @click="showMoreResources">
                <p v-text="buttonText" class="" />
                <PhPlus class="size-6" v-if="!showMore" />
                <PhMinus class="size-6" v-else />
            </button>
        </div>
    </div>
    <ConfirmDangerModal ref="confirmDanger" />
    <RightColumnGroupModal :show="showModal" @close="showModal = false" :modelClass="resourceableModelClass"
        :name="title" :id="id" :model="resourceable" />
</template>

<style scoped>
.ghost {
    opacity: 0.5;
    background: #c8ebfb;
}
</style>

<script>
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import Tooltip from '@/Components/Elements/Tooltip.vue'
import ResourcePanelItem from '@/Components/Panels/ResourcePanel/ResourcePanelItem.vue'
import ButtonPopoverMenu from '@/Components/Utilities/ButtonPopoverMenu.vue'
import { PhPlus, PhMinus, PhDotsThreeOutline } from '@phosphor-icons/vue'
import { Link } from '@inertiajs/vue3'
import RightColumnGroupModal from '@/Components/Modals/RightColumnGroupModal.vue'
import ConfirmDangerModal from '@/Components/Modals/ConfirmDangerModal.vue'

export default {
    name: 'ResourcePanel',

    components: {
        VerticalSlideTransition,
        ReactiveHeightTransition,
        ResourcePanelItem,
        ButtonPopoverMenu,
        PhPlus,
        PhMinus,
        PhDotsThreeOutline,
        Tooltip,
        Link,
        RightColumnGroupModal,
        ConfirmDangerModal,
    },

    props: {
        id: {
            type: Number,
            required: true,
        },
        title: {
            type: String,
            default: 'Related resources',
        },
        resources: {
            type: Array,
            default: () => [],
        },
        resourceable: {
            type: Object,
            required: true,
        },
        resourceableModelClass: {
            type: String,
            required: true,
        },
        maxNbrItems: {
            type: Number,
            default: 5,
        },
        isToTheRight: {
            type: Boolean,
            default: false,
        },
        can: {
            type: Object,
            required: true,
            validator: (value) => {
                return (
                    value.hasOwnProperty('update') &&
                    value.hasOwnProperty('delete') &&
                    value.hasOwnProperty('addChildren') &&
                    value.hasOwnProperty('message') &&
                    value.hasOwnProperty('archive') &&
                    value.hasOwnProperty('showStatistics')
                )
            },
        },
    },

    data() {
        return {
            showMore: false,
            isEditing: false,
            showMenu: false,
            resourceList: this.resources,
            menuItems: [],
            showModal: false,
        }
    },

    created() {
        this.addMenuItems()
    },

    computed: {
        buttonText() {
            return this.showMore
                ? this.$lang.get('Show less')
                : this.$lang.get('Show more')
        },

        panelTitle() {
            return this.$lang.get(this.title)
        },

        canEditResourceItem() {
            return this.isEditing && this.can?.addChildren
        },
    },

    methods: {
        showMoreResources() {
            this.showMore = !this.showMore
        },

        showRightColumnGrupModal() {
            this.showModal = !this.showModal
        },

        showHideMenu() {
            this.showMenu = !this.showMenu
        },

        deleteGroup() {
            const count = this.resources.length
            const resourcesLeft =
                count === 1
                    ? `1 ${this.$lang.get('resource')}`
                    : `${count} ${this.$lang.get('resources')}`

            let message = this.$lang.get(
                'Are you sure you want to delete <b>:definitive_item_name?</b>',
                {
                    definitive_item_name: this.title,
                }
            )

            if (count > 0) {
                message += this.$lang.get(
                    'There is <b>:items_left</b> that will be removed if you continue. If you want to keep these resources please move them to another title.',
                    { items_left: resourcesLeft }
                )
            }

            this.$refs.confirmDanger
                .confirm({
                    title: this.$lang.get('Delete title'),

                    body: message,
                })
                .then((confirmed) => {
                    if (confirmed) {
                        axios
                            .delete(
                                this.route(
                                    'api.resources.delete_right_column_group'
                                ),
                                {
                                    data: {
                                        id: this.id,
                                    },
                                }
                            )
                            .then((response) => {
                            })
                    }
                })
        },
        addMenuItems() {
            if (this.can?.update) {
                this.menuItems.push({
                    text: this.$lang.get('Edit'),
                    type: 'button',
                    action: () => {
                        this.showRightColumnGrupModal()
                    },
                })
            }
            if (
                this.can?.delete &&
                this.resourceable.formattedRightColumnResources.length > 1
            ) {
                this.menuItems.push({
                    text: this.$lang.get('Delete'),
                    type: 'button',
                    action: () => {
                        this.deleteGroup()
                    },
                })
            }
        },
    },
}
</script>
